import {
  FileText,
  Download,
  Calendar,
  TrendingUp,
  Building2,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

interface Reporte {
  id: number;
  titulo: string;
  periodo: string;
  tipo: "trimestral" | "mensual" | "anual";
  fecha: string;
  tamano: string;
  estado: "publicado" | "borrador" | "revision";
}

const reportes: Reporte[] = [
  {
    id: 1,
    titulo: "Informe Trimestral Q1 2025",
    periodo: "Enero - Marzo 2025",
    tipo: "trimestral",
    fecha: "31/03/2025",
    tamano: "4.7 MB",
    estado: "publicado",
  },
  {
    id: 2,
    titulo: "Informe Trimestral Q4 2024",
    periodo: "Octubre - Diciembre 2024",
    tipo: "trimestral",
    fecha: "31/12/2024",
    tamano: "4.3 MB",
    estado: "publicado",
  },
  {
    id: 3,
    titulo: "Informe Anual 2024",
    periodo: "Año fiscal 2024",
    tipo: "anual",
    fecha: "31/12/2024",
    tamano: "8.2 MB",
    estado: "publicado",
  },
  {
    id: 4,
    titulo: "Informe Trimestral Q3 2024",
    periodo: "Julio - Septiembre 2024",
    tipo: "trimestral",
    fecha: "30/09/2024",
    tamano: "4.1 MB",
    estado: "publicado",
  },
  {
    id: 5,
    titulo: "Informe Mensual Febrero 2025",
    periodo: "Febrero 2025",
    tipo: "mensual",
    fecha: "28/02/2025",
    tamano: "2.1 MB",
    estado: "borrador",
  },
];

const metricas = [
  {
    titulo: "Total descargado",
    valor: "1,247",
    cambio: "+18%",
    tipo: "positivo" as const,
    icono: Download,
  },
  {
    titulo: "Reportes publicados",
    valor: "12",
    cambio: "+3",
    tipo: "positivo" as const,
    icono: FileText,
  },
  {
    titulo: "En revisión",
    valor: "2",
    cambio: "Sin cambios",
    tipo: "neutral" as const,
    icono: Calendar,
  },
  {
    titulo: "Inversores activos",
    valor: "47",
    cambio: "+5",
    tipo: "positivo" as const,
    icono: TrendingUp,
  },
];

export function AdminReportes() {
  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1
          className="text-3xl text-[#0F172A] mb-2"
          style={{ fontWeight: 600 }}
        >
          Reportes
        </h1>
        <p className="text-gray-600">
          Gestión y distribución de informes trimestrales y anuales
        </p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {metricas.map((metrica, i) => {
          const Icon = metrica.icono;
          return (
            <Card key={i} className="border border-gray-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      {metrica.titulo}
                    </p>
                    <p className="text-3xl mb-1" style={{ fontWeight: 600 }}>
                      {metrica.valor}
                    </p>
                    <span
                      className={`text-sm ${
                        metrica.tipo === "positivo"
                          ? "text-[#10B981]"
                          : "text-gray-600"
                      }`}
                      style={{ fontWeight: 500 }}
                    >
                      {metrica.cambio}
                    </span>
                  </div>
                  <div className="w-12 h-12 bg-[#2B59FF]/10 rounded-xl flex items-center justify-center">
                    <Icon size={24} className="text-[#2B59FF]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Filtrar por periodo
          </Button>
          <Button
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Exportar listado
          </Button>
        </div>
        <Button className="bg-[#2B59FF] hover:bg-[#2347CC] text-white">
          + Crear nuevo reporte
        </Button>
      </div>

      {/* Reports List */}
      <div className="grid grid-cols-1 gap-4">
        {reportes.map((reporte) => (
          <Card
            key={reporte.id}
            className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow group"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-6">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-14 h-14 bg-[#EF4444]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText size={28} className="text-[#EF4444]" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3
                        className="text-lg text-[#0F172A]"
                        style={{ fontWeight: 600 }}
                      >
                        {reporte.titulo}
                      </h3>
                      <Badge
                        className={
                          reporte.estado === "publicado"
                            ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20"
                            : reporte.estado === "borrador"
                            ? "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20"
                            : "bg-[#2B59FF]/10 text-[#2B59FF] border-[#2B59FF]/20"
                        }
                      >
                        {reporte.estado === "publicado"
                          ? "Publicado"
                          : reporte.estado === "borrador"
                          ? "Borrador"
                          : "En revisión"}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-6 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-gray-400" />
                        <span>{reporte.periodo}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-gray-400" />
                        <span>{reporte.tamano}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">Publicado:</span>
                        <span>{reporte.fecha}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge className="bg-gray-100 text-gray-700 border-gray-200">
                        {reporte.tipo === "trimestral"
                          ? "Trimestral"
                          : reporte.tipo === "mensual"
                          ? "Mensual"
                          : "Anual"}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <Download size={16} className="mr-2" />
                    Descargar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#2B59FF] hover:text-[#2347CC] hover:bg-[#2B59FF]/10"
                  >
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  >
                    Ver
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="p-6">
            <h3
              className="text-lg text-[#0F172A] mb-4"
              style={{ fontWeight: 600 }}
            >
              Reportes por categoría
            </h3>
            <div className="space-y-4">
              {[
                { categoria: "Trimestrales", cantidad: 8, color: "#2B59FF" },
                { categoria: "Anuales", cantidad: 3, color: "#10B981" },
                { categoria: "Mensuales", cantidad: 12, color: "#F59E0B" },
                { categoria: "Especiales", cantidad: 2, color: "#8B5CF6" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-gray-700">
                      {item.categoria}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm" style={{ fontWeight: 500 }}>
                      {item.cantidad} reportes
                    </span>
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          backgroundColor: item.color,
                          width: `${(item.cantidad / 25) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="p-6">
            <h3
              className="text-lg text-[#0F172A] mb-4"
              style={{ fontWeight: 600 }}
            >
              Próximos vencimientos
            </h3>
            <div className="space-y-4">
              {[
                {
                  titulo: "Informe Trimestral Q2 2025",
                  fecha: "30 Jun 2025",
                  dias: 45,
                },
                {
                  titulo: "Reporte Mensual Marzo",
                  fecha: "31 Mar 2025",
                  dias: 14,
                },
                {
                  titulo: "Actualización Portfolio",
                  fecha: "15 Mar 2025",
                  dias: 7,
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                >
                  <div className="w-2 h-2 bg-[#2B59FF] rounded-full mt-2 flex-shrink-0"></div>
                  <div className="flex-1">
                    <p
                      className="text-sm text-[#0F172A]"
                      style={{ fontWeight: 500 }}
                    >
                      {item.titulo}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">{item.fecha}</p>
                  </div>
                  <Badge
                    className={
                      item.dias <= 7
                        ? "bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/20"
                        : "bg-gray-100 text-gray-700 border-gray-200"
                    }
                  >
                    {item.dias} días
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Distribution Activity */}
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <h3
            className="text-lg text-[#0F172A] mb-4"
            style={{ fontWeight: 600 }}
          >
            Actividad de distribución
          </h3>
          <div className="space-y-3">
            {[
              {
                accion: "Enviado",
                reporte: "Informe Q1 2025",
                destinatarios: "47 inversores",
                tiempo: "Hace 2 horas",
              },
              {
                accion: "Descargado",
                reporte: "Informe Anual 2024",
                usuario: "María González",
                tiempo: "Hace 4 horas",
              },
              {
                accion: "Publicado",
                reporte: "Informe Q1 2025",
                usuario: "Usuario",
                tiempo: "Hace 6 horas",
              },
              {
                accion: "Descargado",
                reporte: "Informe Q4 2024",
                usuario: "Carlos Méndez",
                tiempo: "Hace 1 día",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 pb-3 border-b border-gray-100 last:border-0 last:pb-0"
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    item.accion === "Enviado"
                      ? "bg-[#2B59FF]/10"
                      : item.accion === "Publicado"
                      ? "bg-[#10B981]/10"
                      : "bg-[#F59E0B]/10"
                  }`}
                >
                  <FileText
                    size={16}
                    className={
                      item.accion === "Enviado"
                        ? "text-[#2B59FF]"
                        : item.accion === "Publicado"
                        ? "text-[#10B981]"
                        : "text-[#F59E0B]"
                    }
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-[#0F172A]">
                    <span style={{ fontWeight: 500 }}>{item.accion}</span> ·{" "}
                    {item.reporte}
                  </p>
                  <p className="text-xs text-gray-600">
                    {item.destinatarios || item.usuario} · {item.tiempo}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
